import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  Plus,
  Search,
  Trash2,
  User,
  Users,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  X
} from 'lucide-react';

const Team = () => {
  const { user, createUser } = useAuth();
  const { canView, canManageTeam } = usePermissions();

  const canCreateUsers = () => {
    if (!user) return false;
    return user.role === 'yonetici';
  };

  const canEditUsers = () => {
    if (!user) return false;
    return user.role === 'yonetici' || canManageTeam();
  };

  const canDeleteUsers = () => {
    if (!user) return false;
    return user.role === 'yonetici';
  };

  // State variables
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'alt_yonetici',
    permissions: {
      apps: { view: true, create: false, edit: false, delete: false },
      websites: { view: true, create: false, edit: false, delete: false },
      designs: { view: true, create: false, edit: false, delete: false },
      pricing: { view: true, create: false, edit: false, delete: false },
      analytics: { view: true, export: false },
      team: { view: true, manage: false },
      reports: { view: true, generate: false }
    }
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/team');
      if (response.data.success) {
        setTeamMembers(response.data.users || []);
      }
    } catch (error) {
      console.error('Ekip listesi hatası:', error);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      const userId = selectedUser._id || selectedUser.id;
      const response = await axios.delete(`/team/${userId}`);

      if (response.data.success) {
        setTeamMembers(teamMembers.filter(member => (member._id || member.id) !== userId));
        setShowDeleteModal(false);
        setSelectedUser(null);
        alert('Kullanıcı başarıyla silindi!');
      }
    } catch (error) {
      console.error('Kullanıcı silme hatası:', error);
      alert('Kullanıcı silinirken hata oluştu!');
    }
  };

  const handleToggleUserStatus = async (userId, newStatus) => {
    try {
      const response = await axios.patch(`/team/${userId}/status`, {
        isActive: newStatus
      });

      if (response.data.success) {
        setTeamMembers(teamMembers.map(member =>
          (member._id || member.id) === userId
            ? { ...member, isActive: newStatus }
            : member
        ));
        alert(`Kullanıcı ${newStatus ? 'aktif' : 'pasif'} hale getirildi!`);
      }
    } catch (error) {
      console.error('Kullanıcı durum değiştirme hatası:', error);
      alert('İşlem sırasında hata oluştu!');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUser(newUser);
      setShowAddModal(false);
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'alt_yonetici',
        permissions: {
          apps: { view: true, create: false, edit: false, delete: false },
          websites: { view: true, create: false, edit: false, delete: false },
          designs: { view: true, create: false, edit: false, delete: false },
          pricing: { view: true, create: false, edit: false, delete: false },
          analytics: { view: true, export: false },
          team: { view: false, manage: false },
          reports: { view: true, generate: false }
        }
      });
      fetchTeamMembers();
    } catch (error) {
      console.error('Kullanıcı oluşturma hatası:', error);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      ...user,
      password: '' // Şifre alanını boş bırak
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...editingUser };
      // Şifre boşsa güncelleme verisinden çıkar
      if (!updateData.password) {
        delete updateData.password;
      }
      
      const response = await axios.put(`/team/${editingUser._id}`, updateData);
      if (response.data.success) {
        setShowEditModal(false);
        setEditingUser(null);
        fetchTeamMembers();
        alert('Kullanıcı başarıyla güncellendi!');
      }
    } catch (error) {
      console.error('Kullanıcı güncelleme hatası:', error);
      alert(error.response?.data?.message || 'Kullanıcı güncellenirken hata oluştu');
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'yonetici': 'Yönetici',
      'alt_yonetici': 'Alt Yönetici',
      'gelistirici': 'Geliştirici',
      'blogger': 'Blogger',
      'analizci': 'Analizci',
      'pazarlamaci': 'Pazarlamacı',
      'tasarimci': 'UX/UI Tasarımcı'
    };
    return roleNames[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      'yonetici': 'bg-red-100 text-red-800',
      'alt_yonetici': 'bg-blue-100 text-blue-800',
      'gelistirici': 'bg-green-100 text-green-800',
      'blogger': 'bg-yellow-100 text-yellow-800',
      'analizci': 'bg-purple-100 text-purple-800',
      'pazarlamaci': 'bg-orange-100 text-orange-800',
      'tasarimci': 'bg-pink-100 text-pink-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMembers = teamMembers.filter(member =>
    member.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getRoleDisplayName(member.role).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Yetki kontrolü
  if (!canView('team')) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-red-800">
                Erişim Yetkisi Yok
              </h3>
              <p className="mt-2 text-sm text-red-700">
                Ekip yönetimi sayfasını görüntülemek için gerekli yetkiniz bulunmuyor.
                Lütfen yöneticinizle iletişime geçin.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Boş ekip durumu
  if (!loading && teamMembers.length === 0) {
    return (
      <div className="space-y-6">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ekip Yönetimi</h1>
            <p className="mt-2 text-sm text-gray-700">
              Ekip üyelerini yönetin ve yetkilendirin
            </p>
          </div>
          {canCreateUsers() && (
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Yeni Üye Ekle
              </button>
            </div>
          )}
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-yellow-800">
                Henüz Ekip Üyesi Eklenmemiş
              </h3>
              <p className="mt-2 text-sm text-yellow-700">
                Ekip yönetimi için önce ekip üyelerini eklemeniz gerekiyor.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Başlık ve Eylemler */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ekip Yönetimi</h1>
          <p className="mt-2 text-sm text-gray-700">
            Ekip üyelerini yönetin ve yetkilendirin
          </p>
        </div>
        {canCreateUsers() && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Yeni Üye Ekle
            </button>
          </div>
        )}
      </div>

      {/* Arama */}
      {teamMembers.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Ekip üyesi ara..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Ekip Üyeleri Listesi */}
      {filteredMembers.length > 0 && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredMembers.map((member) => (
              <li key={member._id || member.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12">
                      <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-600" />
                      </div>
                    </div>

                    <div className="ml-4">
                      <div className="flex items-center">
                        <h3 className="text-lg font-medium text-gray-900">{member.username}</h3>
                        <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(member.role)}`}>
                          {getRoleDisplayName(member.role)}
                        </span>
                        {member.isActive ? (
                          <CheckCircle className="ml-2 h-5 w-5 text-green-500" title="Aktif" />
                        ) : (
                          <XCircle className="ml-2 h-5 w-5 text-red-500" title="Pasif" />
                        )}
                      </div>

                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <Mail className="h-4 w-4 mr-1" />
                        {member.email}
                      </div>

                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          Son giriş: {member.lastLogin ? formatDate(member.lastLogin) : 'Hiç giriş yapmamış'}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Katılım: {formatDate(member.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {canEditUsers() && (
                      <button
                        onClick={() => handleEditUser(member)}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                        title="Düzenle"
                      >
                        Düzenle
                      </button>
                    )}
                    
                    {canEditUsers() && (
                      <button
                        onClick={() => handleToggleUserStatus(
                          member._id || member.id,
                          !member.isActive
                        )}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${member.isActive
                          ? 'bg-red-100 text-red-800 hover:bg-red-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        title={member.isActive ? 'Pasif Yap' : 'Aktif Yap'}
                      >
                        {member.isActive ? 'Pasif Yap' : 'Aktif Yap'}
                      </button>
                    )}

                    {canDeleteUsers() && member.username !== user?.username && (
                      <button
                        onClick={() => {
                          setSelectedUser(member);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 rounded-full hover:bg-red-100 text-red-600"
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}

                    {!canEditUsers() && !canDeleteUsers() && canView('team') && (
                      <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                        Sadece Görüntüleme
                      </span>
                    )}
                  </div>
                </div>

                {/* Yetki Bilgileri */}
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Uygulamalar</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <div className="flex space-x-1">
                        {member.permissions?.apps?.view && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Görüntüle</span>}
                        {member.permissions?.apps?.create && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Oluştur</span>}
                        {member.permissions?.apps?.edit && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Düzenle</span>}
                        {member.permissions?.apps?.delete && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Sil</span>}
                      </div>
                    </dd>
                  </div>

                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Analitik</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <div className="flex space-x-1">
                        {member.permissions?.analytics?.view && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Görüntüle</span>}
                        {member.permissions?.analytics?.export && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Dışa Aktar</span>}
                      </div>
                    </dd>
                  </div>

                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ekip</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <div className="flex space-x-1">
                        {member.permissions?.team?.view && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Görüntüle</span>}
                        {member.permissions?.team?.manage && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">Yönet</span>}
                      </div>
                    </dd>
                  </div>

                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Raporlar</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <div className="flex space-x-1">
                        {member.permissions?.reports?.view && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Görüntüle</span>}
                        {member.permissions?.reports?.generate && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">Oluştur</span>}
                      </div>
                    </dd>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Yeni Kullanıcı Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Yeni Ekip Üyesi Ekle</h3>

              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Kullanıcı Adı</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Şifre</label>
                  <input
                    type="password"
                    required
                    minLength="6"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Rol</label>
                  <select
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  >
                    <option value="alt_yonetici">Alt Yönetici</option>
                    <option value="gelistirici">Geliştirici</option>
                    <option value="blogger">Blogger</option>
                    <option value="analizci">Analizci</option>
                    <option value="pazarlamaci">Pazarlamacı</option>
                    <option value="tasarimci">UX/UI Tasarımcı</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                  >
                    Oluştur
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Kullanıcı Düzenleme Modalı */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Kullanıcı Düzenle</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingUser(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Kullanıcı Adı</label>
                  <input
                    type="text"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingUser.username}
                    onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Yeni Şifre (Boş bırakılırsa değişmez)</label>
                  <input
                    type="password"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingUser.password}
                    onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rol</label>
                  <select
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  >
                    <option value="alt_yonetici">Alt Yönetici</option>
                    <option value="gelistirici">Geliştirici</option>
                    <option value="blogger">Blogger</option>
                    <option value="analizci">Analizci</option>
                    <option value="pazarlamaci">Pazarlamacı</option>
                    <option value="tasarimci">UX/UI Tasarımcı</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                >
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Kullanıcı Silme Onay Modalı */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div className="mt-3 text-center">
                <h3 className="text-lg font-medium text-gray-900">Kullanıcıyı Sil</h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    <strong>{selectedUser.username}</strong> kullanıcısını silmek istediğinizden emin misiniz?
                    Bu işlem geri alınamaz.
                  </p>
                </div>
                <div className="flex justify-center space-x-3 mt-4">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md hover:bg-gray-400"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleDeleteUser}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md hover:bg-red-700"
                  >
                    Sil
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;